import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Clock, ArrowRight, ArrowDown } from 'lucide-react';

// Landingpage-Pfad pro Modul (per Index, da Items aus i18n kommen).
// Modul 3 (KI Integration) hat keine eigene Landing — individuelles Angebot.
const MODULE_LANDING_PATHS = [
  '/ki-ready-check',
  '/ki-strategie-workshop',
  null,
];

// renderSideContent (optional): wenn gesetzt, ersetzt die rechte (Quote/KPI)-Spalte
// pro Modul durch eigenen Inhalt. Signatur: (index, item, onRight) => ReactNode.
// Pillars nutzen das, um die KPIs gegen Held-Gedanken auszutauschen — Modul-Karten
// (Titel/Preis/Bullets/CTA) bleiben 1:1 wie auf der Homepage.
export default function ServicesSection({ renderSideContent }) {
  const { t, lang } = useLanguage();
  const items = t('services.items') || [];
  const ctaLabel = t('services.cta_label') || 'Gespräch vereinbaren';

  const roadmapRef = useRef(null);
  // Linie zeichnet sich beim Scrollen mit (von 0 bis 100% Höhe).
  const { scrollYProgress } = useScroll({
    target: roadmapRef,
    offset: ['start 0.85', 'end 0.15'],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  // Logo zeigt sich erst sobald Scroll beginnt und verschwindet am Ende.
  const logoOpacity = useTransform(scrollYProgress, [0, 0.02, 0.97, 1], [0, 1, 1, 0]);
  // Sanfte Skalierung beim Start für "Aufwach"-Effekt
  const logoScale = useTransform(scrollYProgress, [0, 0.05], [0.6, 1]);

  return (
    <section id="services" data-testid="services-section" className="bg-white py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        {/* Header */}
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.6 }}
          data-testid="services-label" className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-4 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
          {t('services.label')}
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="services-title" className="text-[#1f1f1f] text-3xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[1.05] mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
          {t('services.title')}
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.7, delay: 0.2 }}
          className="text-[#1f1f1f]/50 text-base md:text-xl mb-8 max-w-3xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {t('services.subtitle')}
        </motion.p>

        {/* ROAD-Trip Tagline — visueller Anker vor der Roadmap (Orange für Kontrast auf Weiß) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, delay: 0.3 }}
          data-testid="roadtrip-tagline"
          className="flex items-center gap-4 mb-16 md:mb-20"
        >
          <span className="h-0.5 w-12 md:w-16 bg-[#e07a2f]" aria-hidden="true" />
          <p className="text-[#e07a2f] text-xl md:text-3xl lg:text-4xl font-bold uppercase tracking-[0.12em]" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {lang === 'de' ? 'Starte deinen ROAD Trip' : 'Start your ROAD trip'}
          </p>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
            className="text-[#e07a2f]"
          >
            <ArrowDown size={28} strokeWidth={2.5} />
          </motion.span>
        </motion.div>

        {/* Roadmap */}
        <div ref={roadmapRef} className="relative">
          {/* Vertikale Spur — hellgrauer Hintergrund */}
          <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-[#e5e5e5]" aria-hidden="true" />
          {/* Vertikale Spur — Lime-Strich, wächst beim Scrollen */}
          <motion.div
            style={{ height: lineHeight }}
            className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 w-0.5 bg-[#befa0a] origin-top"
            aria-hidden="true"
          />
          {/* ENBET-Logo "fährt" entlang der Linie — Position = Ende der lime-Linie */}
          {/* Wichtig: ALLE Transform-Werte (x, y, scale) via framer-motion-style,
              damit Tailwind-translate-Klassen nicht mit motion.div konkurrieren. */}
          <motion.div
            data-testid="roadtrip-logo"
            style={{
              top: lineHeight,
              opacity: logoOpacity,
              scale: logoScale,
              x: '-50%',
              y: '-50%',
            }}
            className="absolute left-6 md:left-1/2 z-20 pointer-events-none"
            aria-hidden="true"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#0a0a0a] flex items-center justify-center shadow-[0_0_0_3px_#befa0a,0_0_20px_rgba(190,250,10,0.6)]">
              <img src="/assets/logo.webp" alt="" width="200" height="120" loading="lazy" decoding="async" className="h-7 md:h-8 w-auto" />
            </div>
          </motion.div>

          <div className="space-y-16 md:space-y-28">
            {Array.isArray(items) && items.map((item, index) => {
              const onRight = index % 2 === 1; // 0=links, 1=rechts, 2=links
              return (
                <motion.div key={index}
                  data-testid={`service-${index}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: 0.05 }}
                  className="relative grid grid-cols-1 md:grid-cols-2 gap-x-12 md:gap-x-20">

                  {/* Station-Kreis auf der Mittellinie */}
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.4, delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="absolute left-6 md:left-1/2 -translate-x-1/2 top-1 z-10 hidden md:block">
                    <div className="w-14 h-14 rounded-full bg-[#0a0a0a] text-[#befa0a] flex items-center justify-center text-sm font-bold border-4 border-white shadow-[0_0_0_2px_#befa0a]"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {String(index + 1).padStart(2, '0')}
                    </div>
                  </motion.div>
                  {/* Mobile: kleinerer Marker links */}
                  <div className="absolute left-6 -translate-x-1/2 top-1 z-10 md:hidden">
                    <div className="w-10 h-10 rounded-full bg-[#0a0a0a] text-[#befa0a] flex items-center justify-center text-xs font-bold border-4 border-white"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {String(index + 1).padStart(2, '0')}
                    </div>
                  </div>

                  {/* Karte */}
                  <div className={`pl-16 md:pl-0 ${onRight ? 'md:col-start-2' : 'md:col-start-1'}`}>
                    <div className="border border-[#e5e5e5] hover:border-[#befa0a] transition-colors duration-500 p-6 md:p-8">
                      {/* Titel + Preis-Label */}
                      <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.25em] font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        Modul {String(index + 1).padStart(2, '0')}
                      </p>
                      <h3 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight leading-[1.15] mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {item.title}
                      </h3>

                      {/* Preis-Zeile (kompakt, mit Pipe wie im Briefing) */}
                      {(item.price_label || item.subtitle) && (
                        <>
                          <p className="text-[#1f1f1f] text-sm md:text-base font-bold mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                            {item.price_label && <span>{item.price_label}</span>}
                            {item.price_label && item.subtitle && <span className="text-[#1f1f1f]/30 mx-2">|</span>}
                            {item.subtitle && <span className="font-normal text-[#1f1f1f]/70">{item.subtitle}</span>}
                          </p>
                          {item.price_note && (
                            <p className="text-[#658a07] text-xs font-medium mb-5 pb-5 border-b border-[#e5e5e5]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                              {item.price_note}
                            </p>
                          )}
                          {!item.price_note && <div className="mb-5 pb-5 border-b border-[#e5e5e5]" />}
                        </>
                      )}

                      {/* Tagline */}
                      {item.tagline && (
                        <p className="text-[#658a07] text-sm md:text-base italic font-medium leading-snug mb-5 border-l-2 border-[#befa0a] pl-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
                          {item.tagline}
                        </p>
                      )}

                      {/* Text-Absatz */}
                      {item.text && (
                        <p className="text-[#1f1f1f]/70 text-sm md:text-base leading-relaxed mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                          {item.text}
                        </p>
                      )}

                      {/* Bullets */}
                      {Array.isArray(item.bullets) && item.bullets.length > 0 && (
                        <div className="space-y-3 mb-5">
                          {item.bullets.map((b, bi) => {
                            // Backward compat: bullet kann String oder {label, text} sein
                            const label = typeof b === 'object' ? b.label : null;
                            const txt = typeof b === 'object' ? b.text : b;
                            return (
                              <div key={bi} className="flex items-start gap-2">
                                <ArrowRight size={14} className="text-[#befa0a] mt-1 flex-shrink-0" />
                                <p className="text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                                  {label && (
                                    <span className="font-bold text-[#1f1f1f]">{label}: </span>
                                  )}
                                  <span className="text-[#1f1f1f]/65">{txt}</span>
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Dauer */}
                      {item.duration && (
                        <div className="flex items-start gap-2 text-[#1f1f1f]/45 text-xs mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                          <Clock size={12} className="mt-0.5 flex-shrink-0" />
                          <span>{item.duration}</span>
                        </div>
                      )}

                      {/* CTA-Reihe: primär Gespräch, sekundär Details auf Landingpage */}
                      <div className="flex flex-wrap items-center gap-4">
                        <a href="#booking"
                          className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] hover:bg-[#befa0a] hover:text-[#0a0a0a] transition-colors py-3 px-5 text-xs font-bold uppercase tracking-wider"
                          style={{ fontFamily: "'Oswald', sans-serif" }}>
                          {ctaLabel} <ArrowRight size={14} />
                        </a>
                        {MODULE_LANDING_PATHS[index] && (
                          <Link to={MODULE_LANDING_PATHS[index]}
                            className="inline-flex items-center gap-1.5 text-[#1f1f1f] hover:text-[#658a07] transition-colors text-xs font-bold uppercase tracking-wider border-b border-transparent hover:border-[#658a07]"
                            style={{ fontFamily: "'Oswald', sans-serif" }}>
                            Mehr erfahren <ArrowRight size={12} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pull-Quote + KPI-Block — Desktop: gegenueber-Spalte, Mobile: unter der Modul-Karte.
                      Auf Pillars: durch renderSideContent (Held-Gedanken) ersetzt. */}
                  <motion.div
                    initial={{ opacity: 0, x: onRight ? -60 : 60 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className={`flex flex-col justify-center pl-16 md:pl-0 mt-6 md:mt-0 ${onRight ? 'md:col-start-1 md:row-start-1 md:pr-12 md:items-end md:text-right' : 'md:col-start-2 md:row-start-1 md:pl-12'}`}>
                    {typeof renderSideContent === 'function' ? (
                      renderSideContent(index, item, onRight)
                    ) : (<>
                    {item.quote && (
                      <motion.p
                        initial={{ opacity: 0, scale: 0.85, y: 20 }}
                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="text-[#1f1f1f] text-3xl lg:text-5xl font-bold uppercase tracking-tight leading-[1.05] mb-4 max-w-md"
                        style={{ fontFamily: "'Oswald', sans-serif" }}
                      >
                        <span className="text-[#befa0a]">"</span>
                        {item.quote}
                        <span className="text-[#befa0a]">"</span>
                      </motion.p>
                    )}
                    {item.quote_sub && (
                      <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7, delay: 0.75 }}
                        className="text-[#1f1f1f]/55 text-base lg:text-lg leading-relaxed mb-8 max-w-md"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {item.quote_sub}
                      </motion.p>
                    )}
                    {Array.isArray(item.kpis) && item.kpis.length > 0 && (
                      <div className={`flex flex-col gap-3 w-full max-w-sm ${onRight ? 'items-end' : 'items-start'}`}>
                        {item.kpis.map((k, ki) => {
                          // Brand-Regel (color_contrast Memory): auf weißer Section
                          // Lime → Olive (#658a07), Warn → Orange (#e07a2f)
                          const accent = k.style === 'warn' ? '#e07a2f' : '#658a07';
                          return (
                            <motion.div
                              key={ki}
                              initial={{ opacity: 0, x: onRight ? 50 : -50 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true, margin: '-50px' }}
                              transition={{ duration: 0.7, delay: 0.45 + ki * 0.15, ease: [0.16, 1, 0.3, 1] }}
                              className="border-l-4 bg-white px-5 py-4 w-full shadow-sm"
                              style={{ borderColor: accent }}
                            >
                              <p className="text-3xl lg:text-4xl font-bold leading-none mb-2"
                                 style={{ fontFamily: "'Oswald', sans-serif", color: accent }}>
                                {k.value}
                              </p>
                              <p className="text-[#1f1f1f] text-sm font-bold uppercase tracking-wide leading-tight mb-1"
                                 style={{ fontFamily: "'Oswald', sans-serif" }}>
                                {k.label}
                              </p>
                              {k.source && (
                                <p className="text-[#1f1f1f]/40 text-[10px] italic mt-1"
                                   style={{ fontFamily: "'Manrope', sans-serif" }}>
                                  Quelle: {k.source}
                                </p>
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                    </>)}
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
